/**
 * Pure helper for grouping ActionBank rows by buttonType.
 *
 * Kept separate so it is trivially unit-testable without React mount overhead.
 */
import { BankActionInterface } from "../../../interfaces/Bank.interface";

export type ActionButtonTypeName =
  "TOGGLE" | "HOLDBACK" | "TURNBACK" | "TOGGLETIMER" | "ENERGIZER_TURN_BACK" | "SWITCH";

/** Canonical display order for action-panel sections. */
export const ACTION_PANEL_ORDER: ActionButtonTypeName[] = [
  "TOGGLE",
  "HOLDBACK",
  "TURNBACK",
  "TOGGLETIMER",
  "ENERGIZER_TURN_BACK",
  "SWITCH",
];

export interface ActionBankGroups {
  TOGGLE: BankActionInterface[];
  HOLDBACK: BankActionInterface[];
  TURNBACK: BankActionInterface[];
  TOGGLETIMER: BankActionInterface[];
  ENERGIZER_TURN_BACK: BankActionInterface[];
  SWITCH: BankActionInterface[];
}

/** Return an empty groups record initialised to empty arrays. */
export function emptyGroups(): ActionBankGroups {
  return {
    TOGGLE: [],
    HOLDBACK: [],
    TURNBACK: [],
    TOGGLETIMER: [],
    ENERGIZER_TURN_BACK: [],
    SWITCH: [],
  };
}

/**
 * Bucket an array of BankActionInterface rows by their buttonType.
 *
 * Rows with an unrecognised buttonType are silently dropped (forward-compat).
 * The input array is not mutated; rows are sorted by createdAt before
 * bucketing so the display order within each category is deterministic.
 */
export function groupActionsByType(rows: BankActionInterface[]): ActionBankGroups {
  const groups = emptyGroups();

  const sorted = rows.slice().sort((a, b) => {
    const ca = a.createdAt ?? "";
    const cb = b.createdAt ?? "";
    return ca.localeCompare(cb);
  });

  sorted.forEach((row) => {
    const key = row.buttonType as ActionButtonTypeName;
    if (key in groups) {
      groups[key].push(row);
    }
  });

  return groups;
}

/** True when at least one group has rows. */
export function hasAnyActions(groups: ActionBankGroups): boolean {
  return ACTION_PANEL_ORDER.some((k) => groups[k].length > 0);
}
